import invariant from 'invariant';
import { DateTime } from 'luxon';
import DateTimeUtils from './DateTimeUtils';

/**
 * RFC 4648 base32 alphabet. I picked this because:
 * - It's all lowercase, which helps in some case-insensitive environments like
 *   SQL
 * - There are no visually-ambiguous characters (e.g., o vs 0), which IDK seems
 *   kind of nice?
 */
const alphabet = 'abcdefghijklmnopqrstuvwxyz234567';

export function randomString(length: number = 8): string {
  let out = '';
  for (let i = 0; i < length; i++) {
    out += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
  }
  return out;
}

export function randomIntWithinRange(
  min: number,
  max: number,
  randWithSeed?: () => number
): number {
  invariant(min <= max, `min ${min} must be <= max ${max}`);
  const randomNumber = randWithSeed ? randWithSeed() : Math.random();
  invariant(
    randomNumber >= 0 && randomNumber < 1,
    `randomNumbermust be in [0, 1)`
  );
  return Math.floor(randomNumber * (max - min + 1) + min);
}

export function randomDateWithinRange(
  start: Date,
  end: Date,
  randWithSeed?: () => number
): Date {
  const randomNumber = randWithSeed ? randWithSeed() : Math.random();
  invariant(
    randomNumber >= 0 && randomNumber < 1,
    `randomNumbermust be in [0, 1)`
  );
  const randomMillis = Math.floor(
    randomNumber * (end.getTime() - start.getTime())
  );
  // truncate to whole days
  return new Date(
    start.getTime() + randomMillis - (randomMillis % (1000 * 60 * 60 * 24))
  );
}

export function randomDayWithinRange(
  start: DateTime,
  end: DateTime,
  randWithSeed?: () => number
): DateTime {
  const millis = DateTimeUtils.dateTimeDifferenceMillis(start, end);
  const days = millis / 1000 / 60 / 60 / 24;
  const randomNumber = randWithSeed ? randWithSeed() : Math.random();
  invariant(
    randomNumber >= 0 && randomNumber < 1,
    `randomNumbermust be in [0, 1)`
  );
  const randomDays = Math.floor(randomNumber * days);
  return DateTimeUtils.addDaysToDateTime(start, randomDays);
}

export function mulberry32(seed: number): () => number {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}
